import { events } from "@constants/tracking";
import type { BaseItemSchema } from "@memorang/types";
import { SessionReportReviewAnswersDialog } from "@memorang/ui/components/SessionReport/SessionReportReviewAnswersDialog";
import type {
	ReviewAnswersFilterChange,
	ReviewAnswersSubmitSummary,
	SessionReportReviewAnswersDialogProps,
} from "@memorang/ui/components/SessionReport/SessionReportReviewAnswersDialog/types";
import { type ReactElement, useCallback, useMemo, useState } from "react";
import type { GoToReviewSession } from "./useGoToReviewSession";
import type { TrackReportEvent } from "./useReportAnalytics";

type Options = {
	onReview: GoToReviewSession;
	isReviewLoading?: boolean;
	trackReportEvent: TrackReportEvent;
};

type Result = {
	onOpenReviewAnswers: (items: BaseItemSchema[]) => void;
	reviewAnswersDialog: ReactElement<SessionReportReviewAnswersDialogProps>;
};

export const useSessionReportReviewAnswersDialog = ({
	onReview,
	isReviewLoading = false,
	trackReportEvent,
}: Options): Result => {
	const [items, setItems] = useState<BaseItemSchema[]>([]);
	const [visible, setVisible] = useState(false);

	const onOpenReviewAnswers = useCallback(
		(nextItems: BaseItemSchema[]) => {
			trackReportEvent(events.reviewAnswersDialogOpened);
			setItems(nextItems);
			setVisible(true);
		},
		[trackReportEvent],
	);

	const handleClose = useCallback(() => {
		setVisible(false);
		setItems([]);
	}, []);

	const handleFilterChange = useCallback(
		({ facet, value, checked }: ReviewAnswersFilterChange) => {
			trackReportEvent(events.sessionReportFiltered, {
				source: "review_answers_dialog",
				filterType: facet,
				value,
				checked: checked ? 1 : 0,
			});
		},
		[trackReportEvent],
	);

	const handleSubmit = useCallback(
		async (
			filteredItems: BaseItemSchema[],
			{ selectedLabels, isAllSelected }: ReviewAnswersSubmitSummary,
		) => {
			try {
				await onReview(filteredItems, {
					source: "review_answers_dialog",
					isAllSelected: isAllSelected ? 1 : 0,
					...(selectedLabels.length > 0
						? { filters: selectedLabels.join(", ") }
						: {}),
				});
			} finally {
				// Always dismiss so a failed resume does not leave the modal stuck open
				// with loading cleared and no error UI (button onPress does not await).
				handleClose();
			}
		},
		[handleClose, onReview],
	);

	const reviewAnswersDialog = useMemo(
		() => (
			<SessionReportReviewAnswersDialog
				visible={visible}
				sessionItems={items}
				loading={isReviewLoading}
				onClose={handleClose}
				onFilterChange={handleFilterChange}
				onSubmit={handleSubmit}
			/>
		),
		[
			handleClose,
			handleFilterChange,
			handleSubmit,
			isReviewLoading,
			items,
			visible,
		],
	);

	return { onOpenReviewAnswers, reviewAnswersDialog };
};
